import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMenu } from '../../contexts/MenuContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave } = FiIcons;

const CategoryForm = ({ category, onClose }) => {
  const { addCategory, updateCategory, menuCategories } = useMenu();
  const [formData, setFormData] = useState({
    name: category?.name || '',
    icon: category?.icon || '🍽️',
    sortOrder: category?.sortOrder || (menuCategories.length + 1)
  });
  const [error, setError] = useState('');

  const commonIcons = [
    '🥖', '☕', '🥐', '🍰', '🥗', '🍜', '🍕', '🍔', 
    '🌮', '🍱', '🍣', '🍤', '🥘', '🍲', '🥙', '🌯',
    '🥪', '🍳', '🥓', '🧀', '🥞', '🍯', '🥛', '🧊',
    '⭐', '🔥', '💎', '👑', '🎉', '🌟', '✨', '🎁'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    // Check for duplicate names (excluding current category if editing)
    const existingCategory = menuCategories.find(cat => 
      cat.name.toLowerCase() === formData.name.toLowerCase() && 
      cat.id !== category?.id
    );

    if (existingCategory) {
      setError('A category with this name already exists');
      return;
    }

    const categoryData = {
      ...formData,
      name: formData.name.trim(),
      sortOrder: parseInt(formData.sortOrder) || 1
    };

    const result = category 
      ? updateCategory(category.id, categoryData)
      : addCategory(categoryData);

    if (result.success) {
      onClose();
    } else {
      setError(result.error);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-warm-brown/10 p-6 flex items-center justify-between">
            <h2 className="text-2xl font-playfair text-warm-brown">
              {category ? 'Edit Category' : 'Add New Category'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} className="text-warm-brown text-xl" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
              >
                {error}
              </motion.div>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-warm-brown font-medium mb-2">Category Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Sandwiches, Beverages, Desserts"
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>

              <div>
                <label className="block text-warm-brown font-medium mb-2">Sort Order</label>
                <input
                  type="number"
                  min="1"
                  value={formData.sortOrder}
                  onChange={(e) => handleInputChange('sortOrder', e.target.value)}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
                <p className="text-xs text-warm-brown/60 mt-1">
                  Lower numbers appear first in the menu
                </p>
              </div>
            </div>

            {/* Icon Selection */}
            <div>
              <label className="block text-warm-brown font-medium mb-2">Category Icon</label>
              <div className="mb-4">
                <div className="flex items-center gap-3 p-3 bg-cream rounded-lg">
                  <span className="text-3xl">{formData.icon}</span>
                  <div>
                    <p className="text-warm-brown font-medium">Selected Icon</p>
                    <p className="text-warm-brown/70 text-sm">This will appear next to your category name</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-8 gap-2 max-h-48 overflow-y-auto border border-warm-brown/20 rounded-lg p-3">
                {commonIcons.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => handleInputChange('icon', icon)}
                    className={`p-2 text-2xl rounded-lg transition-colors hover:bg-warm-brown/10 ${
                      formData.icon === icon 
                        ? 'bg-warm-brown text-cream' 
                        : 'bg-cream hover:bg-warm-brown/5'
                    }`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
              
              <p className="text-xs text-warm-brown/60 mt-2">
                Click on an icon to select it, or enter a custom emoji below
              </p>
              
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                placeholder="Enter custom emoji"
                className="w-full mt-2 px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                maxLength={2}
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-warm-brown text-cream py-3 rounded-lg font-medium hover:bg-warm-brown/90 transition-colors flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiSave} className="text-sm" />
                {category ? 'Update Category' : 'Add Category'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border border-warm-brown/20 text-warm-brown py-3 rounded-lg font-medium hover:bg-warm-brown/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CategoryForm;